import { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';
import { AutocompleteAPI, ContextualBarAPI, FetchAPI, FieldAPI, TokenAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnAfterFetch, OnChange, OnDynamicContentRender, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, MountEvent, RenderEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { LanguageService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation/lib/context';

import { ReactComponent as OpenSVG } from '../../../resources/assets/commons/open.svg';
import { FragmentNames, SlaGraphicAliases } from '../../codegen';
import { ChartFilter, SimpleMonth } from '../../interfaces';
import { FilterService, SlaService } from '../../services';
import { ChartFilterService } from '../../services/chart-filter-service';
import { Utils } from '../../utils';
import BaseController from '../BaseController';

function mountTokenSLATarget(chartFilter: ChartFilter | undefined, token: TokenAPI): void {
  void SlaService.getTotalTarget(chartFilter)
    .then(response => {
      const textOsTime = MessageService.translate('text.target');
      token.setValue(`${textOsTime}: ${response}`);
      token.setColor('safety-blue');
    });
}

function mountTokenSLAReached(chartFilter: ChartFilter | undefined, token: TokenAPI): void {
  void SlaService.getTotalReached(chartFilter)
    .then(response => {
      const textResolutionTime = MessageService.translate('text.reached');
      token.setValue(`${textResolutionTime}: ${response}`);
      token.setColor('safety-green');
    });
}

@Controller({ fragment: FragmentNames.SLA_GRAPHIC })
export default class SlaGraphicController extends BaseController {

  @Inject(SlaGraphicAliases.TOKEN_SLA_TARGET)
  tokenSLATarget!: TokenAPI;

  @Inject(SlaGraphicAliases.TOKEN_SLA_REACHED)
  tokenSLAReached!: TokenAPI;

  @Inject(SlaGraphicAliases.FIELD_MONTH)
  fieldMoth!: FieldAPI;

  @Inject(SlaGraphicAliases.AUTOCOMPLETE_MONTH)
  autocompleteMonth!: AutocompleteAPI;

  @Inject(SlaGraphicAliases.AUTOCOMPLETE_YEAR)
  autocompleteYear!: AutocompleteAPI;

  @Inject(SlaGraphicAliases.FETCH_MONTH)
  fetchMonth!: FetchAPI;

  @Inject(SlaGraphicAliases.AUTOCOMPLETE_MONTH)
  autoCompleteMonth!: AutocompleteAPI;

  @Inject(SlaGraphicAliases.AUTOCOMPLETE_TIME_COURSE)
  autocompleteTimeCourse!: AutocompleteAPI;

  yearValue: string | null = null;
  monthValue: number[] | null = null;
  timeCourseValue: string | null = null;

  @OnMount({ target: SlaGraphicAliases.LAYOUT_INITIAL })
  onMountLayoutRoot(): void {
    const event = new CustomEvent('updateModulesRoute', { detail: { moduleId: 'sla-graphic' } });
    dispatchEvent(event);
  }

  @OnMount(SlaGraphicAliases.TOKEN_SLA_REACHED)
  onMountTokenSlaReached(): void {
    void mountTokenSLAReached(undefined, this.tokenSLAReached);
  }

  @OnMount(SlaGraphicAliases.TOKEN_SLA_TARGET)
  onMountTokenSlaTarget(): void {
    void mountTokenSLATarget(undefined, this.tokenSLATarget);
  }

  @OnChange(SlaGraphicAliases.AUTOCOMPLETE_MONTH)
  onChangeAutocompleteMonth(event: ChangeEvent<{ value: number }>): void {
    if (event.value && event.value.value) {
      this.monthValue = [];
      this.monthValue.push(event.value.value);
    }

    this.executeSearch();
  }

  @OnMount({ target: SlaGraphicAliases.AUTOCOMPLETE_TIME_COURSE })
  onMountAutocompleteTimeCourse(): void {
    FilterService.mountAutoCompleteTimeCourse(this.autocompleteTimeCourse);
  }

  @OnMount(SlaGraphicAliases.AUTOCOMPLETE_MONTH)
  onMountAutoCompleteMonth(): void {
    this.autoCompleteMonth.setData(FilterService.mountFieldMonth());
    FilterService.onAfterFetchAutocompleteMonth(this.autoCompleteMonth);
  }

  @OnMount(SlaGraphicAliases.CONTEXTUAL_BAR_MONTH)
  onMountContextualBarMonth(event: MountEvent): void {
    (event.target as ContextualBarAPI).setTitle(this.getTableTitle('text.quantity-per-month'));
  }

  @OnAfterFetch(SlaGraphicAliases.FETCH_YEAR)
  onAfterFetch(): void {
    FilterService.onAfterFetchAutocompleteYear(this.autocompleteYear);
    setTimeout(() => this.executeSearch(), 500);
  }

  @OnChange(SlaGraphicAliases.AUTOCOMPLETE_YEAR)
  onChangeAutocompleteYear(event: ChangeEvent<{ value: string }>): void {
    this.yearValue = event.value ? event.value.value : null;
    this.executeSearch();
  }

  @OnChange({ target: SlaGraphicAliases.AUTOCOMPLETE_TIME_COURSE })
  onChangeAutocompleteTimeCourse(event: ChangeEvent<{ value: string }>): void {
    this.timeCourseValue = event.value ? event.value.value : null;
    const internalValue = this.autocompleteTimeCourse.getValue();

    this.autoCompleteMonth.setValue(undefined);
    this.autocompleteYear.setValue(undefined);
    this.fieldMoth.setValue(undefined);
    this.autoCompleteMonth.setEnabled(!this.timeCourseValue);
    this.autocompleteYear.setEnabled(!this.timeCourseValue);

    if (internalValue && internalValue.length === 0) {
      FilterService.onAfterFetchAutocompleteMonth(this.autoCompleteMonth);
      FilterService.onAfterFetchAutocompleteYear(this.autocompleteYear);
    }
    setTimeout(() => {
      this.executeSearch();
    }, 500);

  }

  @OnDynamicContentRender(SlaGraphicAliases.TABLE_RESOLUTION_TIME_BY_MONTH)
  onRenderTableMonth(event: RenderEvent): void {
    switch (event.entry) {
      case 'month':
        event.render((data: SimpleMonth): ReactElement => {
          const locale = LanguageService.getCurrentLanguage();
          const month = Intl.DateTimeFormat(locale, {
            month: 'long',
          }).format(new Date(0, parseInt(data.month) - 1));
          return Utils.renderTableText(Utils.capitalizeFirst(month));
        });
        break;
      case 'actions':
        event.render((data: SimpleMonth): ReactElement => {
          return Utils.renderActionButtons([
            {
              title: 'open.action',
              icon: OpenSVG,
              onClick: () => {
                applicationContext.openModule(`/application/sla-os/${data.year}/${data.month}/${data.typeSla ?? '0'}`);
              },
            },
          ]);
        });
        break;
    }
  }

  executeSearch(): void {
    ChartFilterService.executeSearch({
      // autoCompleteYear: this.autocompleteYear,
      valueYear: this.yearValue,
      // autoCompleteMonth: this.autocompleteMonth,
      valueMonth: this.monthValue,
      // autocompleteTimeCourse: this.autocompleteTimeCourse,
      valueTimeCourse: this.timeCourseValue,
      fetchs: [
        this.fetchMonth,
      ],
      onExecute: (params: ChartFilter) => {
        void mountTokenSLATarget(params, this.tokenSLATarget);
        void mountTokenSLAReached(params, this.tokenSLAReached);
      },
    });
  }

  private getTableTitle(expressionKey: string): string {
    const module = MessageService.translate('sla.title');
    const expression = MessageService.translate(expressionKey);
    return expression.replace('#@MODULE#@', module);
  }
}
